import {
  NOTO_SCRIPT_GROUPS,
  SCRIPT_FALLBACK_FAMILY,
  fontLabelOf,
  isWriterGenericFamily,
  pickerFonts,
} from '@messenger/lotus/fonts'

/**
 * Build a name → group-id lookup once at module load. Drives partitioning
 * of language-tagged Noto fonts in `partitionFontsForPicker`.
 */
const FONT_NAME_TO_GROUP_ID = new Map(
  NOTO_SCRIPT_GROUPS.flatMap((group) =>
    group.fonts.map((fontName) => [fontName, group.id]),
  ),
)

/**
 * Partition the fonts list for the TinyMCE picker.
 *
 * Regular fonts (no `language` field) sit on top of the picker, sorted
 * alphabetically. Language-tagged fonts (those that have a `language`
 * field) are bucketed into ordered regional groups (`NOTO_SCRIPT_GROUPS`),
 * each alphabetized within. A trailing `ungrouped` bucket catches any
 * language-tagged font not registered in `NOTO_SCRIPT_GROUPS`. Fonts in
 * the excluded set are dropped entirely.
 *
 * Exported for unit testing — see `tinyMCEFonts.unit.test.js`.
 */
export const partitionFontsForPicker = (fonts, excluded) => {
  const excludedSet = excluded instanceof Set ? excluded : new Set(excluded)
  const filtered = fonts.filter((font) => !excludedSet.has(font.name))
  const regular = filtered
    .filter((font) => !font.language && font.language !== '')
    .slice()
    .sort((a, b) => a.name.localeCompare(b.name))
  const languageFonts = filtered.filter(
    (font) => 'language' in font && font.language !== undefined,
  )
  const grouped = NOTO_SCRIPT_GROUPS.map((group) => {
    const members = languageFonts
      .filter((font) => FONT_NAME_TO_GROUP_ID.get(font.name) === group.id)
      .slice()
      .sort((a, b) => a.name.localeCompare(b.name))
    return { id: group.id, label: group.label, fonts: members }
  }).filter((group) => group.fonts.length > 0)
  const ungrouped = languageFonts
    .filter((font) => !FONT_NAME_TO_GROUP_ID.has(font.name))
    .slice()
    .sort((a, b) => a.name.localeCompare(b.name))
  return { regular, grouped, ungrouped }
}

/**
 * Insert `SCRIPT_FALLBACK_FAMILY` into a font's fallback chain unless it's
 * already present. Position: just before the first generic family, one of the
 * six the TinyMCE writers have always read (`isWriterGenericFamily`), so the
 * script fallback gets a shot at non-Latin codepoints before the generic
 * resolves to a system font. Each generic resolves to a system font with
 * broad codepoint coverage, so a generic ahead of the script fallback would
 * catch every codepoint first. If no generic is present, append at the end.
 * See #2517 + script-fallback.css.
 */
const appendScriptFallback = (fallback) => {
  if (fallback.includes(SCRIPT_FALLBACK_FAMILY)) return fallback
  const firstGenericIdx = fallback.findIndex(isWriterGenericFamily)
  if (firstGenericIdx === -1) return [...fallback, SCRIPT_FALLBACK_FAMILY]
  return [
    ...fallback.slice(0, firstGenericIdx),
    SCRIPT_FALLBACK_FAMILY,
    ...fallback.slice(firstGenericIdx),
  ]
}

const toMenuEntry = (font) => {
  const fallback = font.fallback ?? []
  const family = [font.name, ...appendScriptFallback(fallback)].join(',')
  return { name: font.name, displayName: fontLabelOf(font), family }
}

/**
 * Build the structured data model consumed by `customFontSelect`.
 *
 * The picker renders Latin / language-agnostic fonts at the top level, then
 * one nested submenu per regional Noto script group (Middle Eastern, South
 * Asian, etc.). Any language-tagged font missing from `NOTO_SCRIPT_GROUPS`
 * falls into a trailing "Other" submenu so it stays discoverable. Each entry
 * exposes `name` (the bare family for matching the current selection's
 * `font-family`), `displayName` (what the user sees), and `family` (the
 * comma-separated value to pass to `FontName`, with the script fallback
 * pre-inserted — see #2517).
 *
 * Exported for unit testing — see `tinyMCEFonts.unit.test.js`.
 */
export const buildFontMenuModel = (fonts, excluded) => {
  const { regular, grouped, ungrouped } = partitionFontsForPicker(
    fonts,
    excluded,
  )
  return {
    regular: regular.map(toMenuEntry),
    groups: grouped.map((group) => ({
      id: group.id,
      label: group.label,
      fonts: group.fonts.map(toMenuEntry),
    })),
    other:
      ungrouped.length > 0
        ? { id: 'other', label: 'Other', fonts: ungrouped.map(toMenuEntry) }
        : null,
  }
}

export const tinyMCEFontMenuModel = buildFontMenuModel(pickerFonts, [])
